/**
 * Spese da riaddebitare e rendiconti in lavorazione, raccolti per commessa.
 *
 * Spese e SAL vivono dentro i rendiconti, uno per persona: chi fattura non
 * li vede finché qualcuno non approva, e un rimborso chilometrico di
 * settembre si scopre a dicembre. Qui si vedono tutti insieme — esterni e
 * interni sulla stessa commessa — con il collegamento al rendiconto da
 * approvare, che è il gesto che li porta in fattura.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fatturazionIApi } from '../api';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtEur = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

const CATEGORIE = {
  km: 'Chilometri', telepass: 'Telepass/pedaggi', vitto: 'Vitto',
  alloggio: 'Alloggio', parking: 'Parcheggio', altro: 'Altro',
};
const STATO_SAL = {
  bozza: { l: 'in stesura', c: 'bg-gray-100 text-gray-600' },
  inviato: { l: 'da approvare', c: 'bg-blue-100 text-blue-700' },
};

export default function DaRiaddebitare({ onCambiato, clienteId }) {
  const [dati0, setDati] = useState(null);
  const [aperte, setAperte] = useState({});

  useEffect(() => { fatturazionIApi.daRiaddebitare().then(setDati).catch(() => setDati(null)); }, []);

  if (!dati0) return <div className="text-sm text-gray-400 py-8 text-center">Caricamento…</div>;

  // Il filtro cliente della pagina vale anche qui, totali compresi.
  const dati = !clienteId ? dati0 : (() => {
    const commesse = dati0.commesse.filter(g => String(g.cliente_id) === String(clienteId));
    const somma = (campo) => +commesse.reduce((t, g) => t + Number(g[campo] || 0), 0).toFixed(2);
    return { commesse, totale_spese: somma('totale_spese'),
             totale_al_cliente: somma('totale_al_cliente'), totale_sal: somma('totale_sal') };
  })();

  if (!dati.commesse.length) {
    return (
      <div className="card text-center py-10 text-gray-400">
        {clienteId
          ? 'Per questo cliente non ci sono spese da riaddebitare né rendiconti in lavorazione.'
          : 'Nessuna spesa da riaddebitare e nessun rendiconto in lavorazione.'}
      </div>
    );
  }

  const apri = (id) => setAperte(a => ({ ...a, [id]: !a[id] }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Spese da riaddebitare</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{fmt(dati.totale_al_cliente ?? dati.totale_spese)}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            da fatturare al cliente alle tariffe dell'offerta
            {dati.totale_al_cliente != null && dati.totale_al_cliente !== dati.totale_spese && (
              <> · {fmt(dati.totale_spese)} di rimborsi a chi le ha anticipate</>
            )}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Rendiconti in lavorazione</div>
          <div className="text-2xl font-bold text-brand-600 mt-1">{fmt(dati.totale_sal)}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            valore orientativo: l'importo si decide approvando
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Commesse coinvolte</div>
          <div className="text-2xl font-bold text-gray-700 mt-1">{dati.commesse.length}</div>
          <div className="text-xs text-gray-400 mt-0.5">
            approvando il rendiconto, spese comprese, nasce la fattura
          </div>
        </div>
      </div>

      {dati.commesse.map(g => {
        const aperta = !!aperte[g.commessa_id];
        return (
          <div key={g.commessa_id} className="card p-0 overflow-hidden">
            <button className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
                    onClick={() => apri(g.commessa_id)}>
              <span className="text-gray-400 mt-0.5">{aperta ? '▾' : '▸'}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  {/* Qualche commessa storica è senza numero: meglio dirlo che
                      mostrare uno spazio vuoto dove si cerca il riferimento. */}
                  <span className="font-mono text-xs text-brand-700">
                    {g.commessa_numero?.trim() || <span className="italic text-gray-400">senza numero</span>}
                  </span>
                  <span className="font-medium text-gray-900">{g.cliente_nome || '—'}</span>
                </div>
                <div className="text-xs text-gray-500 truncate">{g.commessa_titolo}</div>
              </div>
              <div className="text-right shrink-0">
                {g.totale_spese > 0 && (
                  <div className="text-sm font-semibold text-amber-700">
                    {fmt(g.totale_al_cliente ?? g.totale_spese)}
                    <span className="font-normal text-xs text-gray-500"> di spese al cliente</span>
                  </div>
                )}
                {g.sal.length > 0 && (
                  <div className="text-xs text-gray-500">
                    {g.sal.length} {g.sal.length === 1 ? 'rendiconto' : 'rendiconti'}
                    {g.totale_sal > 0 && <> · {fmt(g.totale_sal)}</>}
                  </div>
                )}
              </div>
            </button>

            {aperta && (
              <div className="border-t bg-gray-50/60 px-4 py-3 space-y-4">
                <Link to={`/commesse/${g.commessa_id}`} className="text-xs text-brand-600 hover:underline">
                  Apri la scheda della commessa →
                </Link>
                {/* I rendiconti: è da qui che spese e attività diventano fattura */}
                {g.sal.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                      Rendiconti aperti
                    </div>
                    <div className="space-y-1.5">
                      {g.sal.map(s => {
                        const st = STATO_SAL[s.stato] || { l: s.stato, c: 'bg-gray-100 text-gray-600' };
                        return (
                          <div key={s.id} className="bg-white border rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap">
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${st.c}`}>{st.l}</span>
                            <div className="flex-1 min-w-[160px]">
                              <div className="text-sm text-gray-800">{s.descrizione || 'Avanzamento lavori'}</div>
                              <div className="text-xs text-gray-500">
                                {fmtDate(s.data_sal)}
                                {s.autore && <> · {s.autore}</>}
                                {Number(s.percentuale) > 0 && <> · {s.percentuale}% dichiarato</>}
                                {s.n_attivita > 0 && <> · {s.n_attivita} {s.n_attivita === 1 ? 'attività' : 'attività'}</>}
                                {Number(s.spese) > 0 && <> · {fmt(s.spese)} di spese</>}
                              </div>
                            </div>
                            <div className="text-sm font-medium text-gray-700">{fmt(s.valore_stimato)}</div>
                            <Link to={`/sal?apri=${s.id}`} className="btn-secondary btn-sm shrink-0">
                              Apri e approva
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Le spese riga per riga: chi le ha anticipate e se c'è il giustificativo */}
                {g.spese.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                      Spese anticipate da riaddebitare
                    </div>
                    <div className="bg-white border rounded-lg overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b">
                          <tr className="text-left text-xs text-gray-500">
                            <th className="px-3 py-2">Data</th>
                            <th className="px-3 py-2">Chi l'ha anticipata</th>
                            <th className="px-3 py-2">Categoria</th>
                            <th className="px-3 py-2">Descrizione</th>
                            <th className="px-3 py-2 text-right">Rimborso</th>
                            <th className="px-3 py-2 text-right">Al cliente</th>
                            <th className="px-3 py-2">Rendiconto</th>
                            <th className="px-3 py-2">Giustificativo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.spese.map(sp => (
                            <tr key={sp.id} className="border-b last:border-0">
                              <td className="px-3 py-2 text-gray-600 text-xs">{fmtDate(sp.data)}</td>
                              <td className="px-3 py-2">
                                {sp.chi || '—'}
                                <span className={`ml-1.5 text-[10px] px-1 py-0.5 rounded ${
                                  sp.interno ? 'bg-brand-50 text-brand-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {sp.interno ? 'interno' : 'esterno'}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-600">
                                {CATEGORIE[sp.categoria] || sp.categoria}
                                {sp.km && <span className="text-xs text-gray-400"> · {sp.km} km</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-600">{sp.descrizione || '—'}</td>
                              <td className="px-3 py-2 text-right">{fmtEur(sp.importo)}</td>
                              <td className="px-3 py-2 text-right font-medium">
                                {fmtEur(sp.importo_cliente ?? sp.importo)}
                                {sp.tariffa_km_cliente && (
                                  <span className="block text-[11px] font-normal text-gray-400">{sp.tariffa_km_cliente} €/km</span>
                                )}
                              </td>
                              {/* Da dove dipende: un rendiconto ancora aperto si
                                  approva e le porta in fattura; uno già approvato
                                  no — quelle spese sono rimaste fuori. */}
                              <td className="px-3 py-2 text-xs">
                                {sp.sal_stato === 'approvato'
                                  ? <span className="text-red-700 font-medium">già approvato</span>
                                  : <span className="text-gray-500">{STATO_SAL[sp.sal_stato]?.l || sp.sal_stato}</span>}
                              </td>
                              <td className="px-3 py-2 text-xs">
                                {sp.ha_giustificativo
                                  ? <span className="text-green-700">presente</span>
                                  : <span className="text-amber-700">manca</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">
                      Entrano in fattura come riga separata dalla consulenza quando approvi il
                      rendiconto che le contiene. Se una non va riaddebitata, togli la spunta
                      nella scheda del rendiconto.
                    </p>
                    {/* Spese su rendiconti già approvati: approvare non serve
                        più, la fattura è stata emessa senza di loro. */}
                    {(() => {
                      const fuori = g.spese.filter(sp => sp.sal_stato === 'approvato');
                      if (!fuori.length) return null;
                      const tot = fuori.reduce((t, sp) => t + Number(sp.importo || 0), 0);
                      return (
                        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2.5 py-2 mt-1.5">
                          {fmtEur(tot)} di spese stanno su un rendiconto <strong>già approvato</strong>:
                          la fattura è uscita senza di loro. Per recuperarle aggiungi una fattura
                          dalla scheda della commessa, oppure rettifica il rendiconto alzando
                          l'importo.
                        </p>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
